import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Plus, Mail, Shield, Eye, Edit3, Trash2, 
  Building, Activity, Clock, UserPlus, LogOut, Check, X,
  Briefcase, ChevronRight, Settings, Lock
} from 'lucide-react';
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppChat from "@/components/WhatsAppChat";
import { startGoogleLogin } from "@/lib/googleAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TeamsPage = () => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [activities, setActivities] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState({ show: false, member: null });
  const [memberPermissions, setMemberPermissions] = useState({});
  const [newTeam, setNewTeam] = useState({ name: '', description: '' });
  const [inviteData, setInviteData] = useState({ email: '', role: 'editor' });
  const [error, setError] = useState('');
  const [requiresLogin, setRequiresLogin] = useState(false);

  useEffect(() => {
    loadTeams();
    loadPendingInvites();
  }, []);

  useEffect(() => {
    if (showPermissionsModal.show && showPermissionsModal.member && selectedTeam) {
      loadMemberPermissions(showPermissionsModal.member.user_id);
    }
  }, [showPermissionsModal.show, showPermissionsModal.member]);

  const loadTeams = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/teams/my-teams`, { withCredentials: true });
      setTeams(response.data.teams);
      if (response.data.teams.length > 0 && !selectedTeam) {
        setSelectedTeam(response.data.teams[0]);
        loadActivities(response.data.teams[0].team_id);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        setRequiresLogin(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadActivities = async (teamId) => {
    try {
      const response = await axios.get(`${API_URL}/api/teams/${teamId}/activities`, { withCredentials: true });
      setActivities(response.data.activities);
    } catch (error) {
      console.error('Failed to load activities:', error);
    }
  };

  const loadPendingInvites = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/teams/invitations/pending`, { withCredentials: true });
      setPendingInvites(response.data.invitations);
    } catch (error) {
      console.error('Failed to load invitations:', error);
    }
  };

  const createTeam = async () => {
    if (!newTeam.name.trim()) {
      setError('Team name is required');
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/api/teams/create`, newTeam, { withCredentials: true });
      setTeams([...teams, response.data.team]);
      setSelectedTeam(response.data.team);
      setShowCreateModal(false);
      setNewTeam({ name: '', description: '' });
      setError('');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create team');
    }
  };

  const inviteMember = async () => {
    if (!inviteData.email.trim()) {
      setError('Email is required');
      return;
    }
    try {
      await axios.post(
        `${API_URL}/api/teams/${selectedTeam.team_id}/invite`,
        inviteData,
        { withCredentials: true }
      );
      setShowInviteModal(false);
      setInviteData({ email: '', role: 'editor' });
      setError('');
      alert('Invitation sent successfully!');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const acceptInvite = async (inviteToken) => {
    try {
      await axios.post(`${API_URL}/api/teams/join?invite_token=${inviteToken}`, {}, { withCredentials: true });
      loadTeams();
      loadPendingInvites();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to accept invitation');
    }
  };

  const removeMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    try {
      await axios.delete(
        `${API_URL}/api/teams/${selectedTeam.team_id}/members/${memberId}`,
        { withCredentials: true }
      );
      loadTeams();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const updateRole = async (memberId, newRole) => {
    try {
      await axios.put(
        `${API_URL}/api/teams/${selectedTeam.team_id}/members/role`,
        { user_id: memberId, role: newRole },
        { withCredentials: true }
      );
      loadTeams();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const loadMemberPermissions = async (memberId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/teams/${selectedTeam.team_id}/members/${memberId}/permissions`,
        { withCredentials: true }
      );
      setMemberPermissions(response.data);
    } catch (error) {
      console.error('Failed to load permissions:', error);
    }
  };

  const updateMemberPermissions = async (memberId, permissions) => {
    try {
      await axios.put(
        `${API_URL}/api/teams/${selectedTeam.team_id}/members/permissions`,
        { user_id: memberId, permissions },
        { withCredentials: true }
      );
      setShowPermissionsModal({ show: false, member: null });
      alert('Permissions updated successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update permissions');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Shield className="w-4 h-4 text-indigo-600" />;
      case 'editor': return <Edit3 className="w-4 h-4 text-green-600" />;
      case 'viewer': return <Eye className="w-4 h-4 text-gray-600" />;
      default: return null;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-indigo-100 text-indigo-700';
      case 'editor': return 'bg-green-100 text-green-700';
      case 'viewer': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatActivityAction = (action) => {
    const actions = {
      'team_created': 'created the team',
      'member_invited': 'invited a new member',
      'member_joined': 'joined the team',
      'member_removed': 'removed a member',
      'role_updated': 'updated member role',
      'workspace_created': 'created a workspace'
    };
    return actions[action] || action;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (requiresLogin) {
    const handleGoogleLogin = () => {
      startGoogleLogin();
    };

    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md">
            <Lock className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Login Required</h2>
            <p className="text-gray-600 mb-6">Please sign in to access Teams and collaborate with your team members.</p>
            <button
              onClick={handleGoogleLogin}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 mx-auto"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
              </svg>
              Sign in with Google
            </button>
          </div>
        </div>
        <Footer />
        <WhatsAppChat />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <div className="flex-1">
        {/* Page Header */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-indigo-600" />
                <h1 className="text-2xl font-bold text-gray-900">Team Management</h1>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Create Team
              </button>
            </div>
          </div>
        </div>

      {/* Pending Invitations */}
      {pendingInvites.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-800 mb-3">Pending Invitations</h3>
            <div className="space-y-2">
              {pendingInvites.map(invite => (
                <div key={invite.invite_id} className="flex items-center justify-between bg-white rounded-lg p-3">
                  <div>
                    <span className="font-medium">{invite.team_name}</span>
                    <span className="text-sm text-gray-500 ml-2">as {invite.role}</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => acceptInvite(invite.invite_token)}
                      className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Teams List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <h2 className="font-semibold text-gray-900 mb-4">My Teams</h2>
              {teams.length === 0 ? (
                <p className="text-gray-500 text-sm">No teams yet. Create one to get started!</p>
              ) : (
                <div className="space-y-2">
                  {teams.map(team => (
                    <button
                      key={team.team_id}
                      onClick={() => {
                        setSelectedTeam(team);
                        loadActivities(team.team_id);
                      }}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        selectedTeam?.team_id === team.team_id
                          ? 'bg-indigo-50 border-indigo-200 border'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold">
                          {team.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{team.name}</p>
                          <p className="text-xs text-gray-500">{team.members.length} members</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Team Details */}
          <div className="lg:col-span-3">
            {selectedTeam ? (
              <div className="space-y-6">
                {/* Team Header */}
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{selectedTeam.name}</h2>
                      <p className="text-gray-500 mt-1">{selectedTeam.description || 'No description'}</p>
                    </div>
                    <button
                      onClick={() => setShowInviteModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors"
                    >
                      <UserPlus className="w-5 h-5" />
                      Invite Member
                    </button>
                  </div>
                </div>

                {/* Members */}
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Team Members</h3>
                  <div className="space-y-3">
                    {selectedTeam.members.map(member => (
                      <div key={member.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg flex-wrap gap-2 sm:flex-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center text-gray-700 font-medium shrink-0">
                            {member.name?.charAt(0)?.toUpperCase() || member.email.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">{member.name || member.email}</p>
                            <p className="text-xs text-gray-500 truncate">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap w-full sm:w-auto justify-end">
                          <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(member.role)}`}>
                            {getRoleIcon(member.role)}
                            {member.role}
                          </span>
                          {member.user_id !== selectedTeam.owner_id && (
                            <div className="flex gap-2">
                              <select
                                value={member.role}
                                onChange={(e) => updateRole(member.user_id, e.target.value)}
                                className="text-sm border rounded px-2 py-1"
                              >
                                <option value="admin">Admin</option>
                                <option value="editor">Editor</option>
                                <option value="viewer">Viewer</option>
                              </select>
                              <button
                                onClick={() => setShowPermissionsModal({ show: true, member })}
                                className="p-1 text-indigo-500 hover:bg-indigo-50 rounded"
                                title="Manage Permissions"
                              >
                                <Settings className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => removeMember(member.user_id)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                          {member.user_id === selectedTeam.owner_id && (
                            <span className="text-xs text-indigo-600 font-medium">Owner</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Activity Log */}
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Activity Log
                  </h3>
                  {activities.length === 0 ? (
                    <p className="text-gray-500 text-sm">No activity yet</p>
                  ) : (
                    <div className="space-y-3">
                      {activities.slice(0, 10).map(activity => (
                        <div key={activity.activity_id} className="flex items-start gap-3 text-sm">
                          <Clock className="w-4 h-4 text-gray-400 mt-0.5" />
                          <div>
                            <span className="font-medium">{activity.user_name}</span>
                            <span className="text-gray-600"> {formatActivityAction(activity.action)}</span>
                            <p className="text-xs text-gray-400">{new Date(activity.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
                <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Team Selected</h3>
                <p className="text-gray-500 mb-4">Select a team from the list or create a new one</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Create Your First Team
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Team Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Team</h2>
            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Team Name</label>
                <input
                  type="text"
                  value={newTeam.name}
                  onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g., Marketing Team"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <textarea
                  value={newTeam.description}
                  onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  rows={3}
                  placeholder="What does this team work on?"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowCreateModal(false); setError(''); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={createTeam}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Create Team
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Invite Team Member</h2>
            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="colleague@company.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={inviteData.role}
                  onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="admin">Admin - Full access</option>
                  <option value="editor">Editor - Can edit content</option>
                  <option value="viewer">Viewer - Read only</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowInviteModal(false); setError(''); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={inviteMember}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {showPermissionsModal.show && showPermissionsModal.member && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Manage Permissions
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {showPermissionsModal.member.name || showPermissionsModal.member.email}
              <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded text-xs">
                {showPermissionsModal.member.role}
              </span>
            </p>

            <div className="space-y-4">
              <div className="border-b pb-2">
                <h4 className="font-medium text-gray-700 text-sm">Team Management</h4>
              </div>
              {[
                { key: 'can_manage_team', label: 'Manage Team Settings' },
                { key: 'can_invite_members', label: 'Invite New Members' },
                { key: 'can_remove_members', label: 'Remove Members' },
                { key: 'can_change_roles', label: 'Change Member Roles' },
              ].map(perm => (
                <label key={perm.key} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                  <span className="text-sm">{perm.label}</span>
                  <input
                    type="checkbox"
                    checked={memberPermissions.permissions?.[perm.key] || false}
                    onChange={(e) => setMemberPermissions(prev => ({
                      ...prev,
                      permissions: { ...prev.permissions, [perm.key]: e.target.checked }
                    }))}
                    className="w-4 h-4 text-indigo-600 rounded"
                  />
                </label>
              ))}

              <div className="border-b pb-2 pt-4">
                <h4 className="font-medium text-gray-700 text-sm">Content & Tasks</h4>
              </div>
              {[
                { key: 'can_edit_content', label: 'Edit Content' },
                { key: 'can_delete_content', label: 'Delete Content' },
                { key: 'can_view_content', label: 'View Content' },
                { key: 'can_create_tasks', label: 'Create Tasks' },
                { key: 'can_assign_tasks', label: 'Assign Tasks to Others' },
                { key: 'can_delete_tasks', label: 'Delete Tasks' },
              ].map(perm => (
                <label key={perm.key} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                  <span className="text-sm">{perm.label}</span>
                  <input
                    type="checkbox"
                    checked={memberPermissions.permissions?.[perm.key] || false}
                    onChange={(e) => setMemberPermissions(prev => ({
                      ...prev,
                      permissions: { ...prev.permissions, [perm.key]: e.target.checked }
                    }))}
                    className="w-4 h-4 text-indigo-600 rounded"
                  />
                </label>
              ))}

              <div className="border-b pb-2 pt-4">
                <h4 className="font-medium text-gray-700 text-sm">Analytics & Billing</h4>
              </div>
              {[
                { key: 'can_view_analytics', label: 'View Analytics' },
                { key: 'can_export_reports', label: 'Export Reports' },
                { key: 'can_manage_billing', label: 'Manage Billing' },
              ].map(perm => (
                <label key={perm.key} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                  <span className="text-sm">{perm.label}</span>
                  <input
                    type="checkbox"
                    checked={memberPermissions.permissions?.[perm.key] || false}
                    onChange={(e) => setMemberPermissions(prev => ({
                      ...prev,
                      permissions: { ...prev.permissions, [perm.key]: e.target.checked }
                    }))}
                    className="w-4 h-4 text-indigo-600 rounded"
                  />
                </label>
              ))}
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button
                onClick={() => setShowPermissionsModal({ show: false, member: null })}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => updateMemberPermissions(
                  showPermissionsModal.member.user_id,
                  memberPermissions.permissions
                )}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Save Permissions
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
      <Footer />
      <WhatsAppChat />
    </div>
  );
};

export default TeamsPage;
